'use client';

import { RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '@/i18n/routing';
import { type ArtistProgramActivity, listActivityPage } from '../../_apis';
import { useApprovedArtistGuard } from '../../_hooks/use-approved-artist-guard';
import { ActivityGrid } from './_components';

export default function ArtistProgramActivitiesPage() {
  const t = useTranslations('artistProgram.activity');
  const locale = useLocale();
  const guard = useApprovedArtistGuard();
  const [items, setItems] = useState<ArtistProgramActivity[]>([]);
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading');
  const [token, setToken] = useState('');
  const [more, setMore] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const page = await listActivityPage({ locale, pageSize: 10 });
      setItems(page.activities);
      setToken(page.nextPageToken);
      setState('success');
    } catch {
      setState('error');
    }
  }, [locale]);
  useEffect(() => {
    if (guard === 'approved') void load();
  }, [guard, load]);
  useEffect(() => {
    if (!token || more || state !== 'success' || !sentinel.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setMore(true);
        void listActivityPage({ locale, pageSize: 10, pageToken: token })
          .then((page) => {
            setItems((current) => [...current, ...page.activities]);
            setToken(page.nextPageToken);
          })
          .finally(() => setMore(false));
      },
      { rootMargin: '320px' },
    );
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [locale, more, state, token]);

  return (
    <section className="relative mx-auto max-w-[1440px] pt-[204px] pb-48 sm:px-4 sm:pt-32">
      <h1 className="mb-9 text-center font-semibold text-[40px]">{t('pageTitle')}</h1>
      {(guard === 'checking' || state === 'loading') && (
        <div className="h-80 animate-pulse rounded-[22px] bg-white/8" />
      )}
      {state === 'error' && (
        <div className="flex min-h-80 flex-col items-center justify-center">
          <p>{t('loadFailed')}</p>
          <button type="button" onClick={load} className="mt-5 rounded-full bg-[#1396ff] px-5 py-3">
            {t('retry')}
          </button>
        </div>
      )}
      {state === 'success' && items.length === 0 && (
        <p className="py-32 text-center text-white/60">{t('noActivities')}</p>
      )}
      {state === 'success' && items.length > 0 && <ActivityGrid activities={items} />}
      <div ref={sentinel} className="flex h-16 justify-center">
        {more && <RefreshCw className="animate-spin" />}
      </div>
    </section>
  );
}
